import { Editor } from '@tiptap/core';
import { Link as LinkExtension } from '@tiptap/extension-link';
import { MarkType } from '@tiptap/pm/model';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { find } from 'linkifyjs';
import { createPluginKeyChecker } from './utils/plugin';

export type SetLinkCommandAttribute = Parameters<Editor['commands']['setLink']>[0];

const handlePasteLinkPluginKey = 'handlePasteLink';

export const Link = LinkExtension.extend({
  addCommands() {
    const commands = this.parent?.() ?? {};
    return {
      ...commands,
      // Changing internals
      setLink:
        (attributes) =>
        ({ chain }) => {
          const link = parseLink(attributes.href, this.options.defaultProtocol);

          return chain()
            .extendMarkRange(this.type)
            .setMark(this.name, link ? { ...attributes, href: link.href } : attributes)
            .setMeta('preventAutolink', true)
            .run();
        },
    };
  },

  addProseMirrorPlugins() {
    const plugins = this.parent?.() ?? [];

    if (this.options.linkOnPaste) {
      const isPluginInstanceOfHandlePasteLinkPluginKey = createPluginKeyChecker(handlePasteLinkPluginKey);
      const index = plugins.findIndex(isPluginInstanceOfHandlePasteLinkPluginKey);

      if (!index) {
        throw new Error('Failed to replace handlePasteLinkPlugin');
      }

      plugins.splice(
        index,
        1,
        pasteHandler({
          editor: this.editor,
          defaultProtocol: this.options.defaultProtocol,
          type: this.type,
        })
      );
    }
    return plugins;
  },
});

type PasteHandlerOptions = {
  editor: Editor;
  defaultProtocol: string;
  type: MarkType;
};

export function pasteHandler(options: PasteHandlerOptions): Plugin {
  return new Plugin({
    key: new PluginKey(handlePasteLinkPluginKey),
    props: {
      handlePaste: (view, event, slice) => {
        const { state } = view;
        const { selection } = state;
        const { empty } = selection;

        if (empty) {
          return false;
        }

        let textContent = '';

        slice.content.forEach((node) => {
          textContent += node.textContent;
        });

        const link = parseLink(textContent, options.defaultProtocol);

        if (!textContent || !link) {
          return false;
        }

        if (options.editor.isActive('link')) {
          options.editor
            .chain()
            .extendMarkRange(options.type)
            .updateAttributes(options.type, {
              href: link.href,
            })
            .run();
        } else {
          options.editor.commands.setMark(options.type, {
            href: link.href,
          });
        }

        return true;
      },
    },
  });
}

function parseLink(href: string, defaultProtocol: string) {
  return find(href, { defaultProtocol: defaultProtocol }).find((item) => item.isLink && item.value === href);
}
